// 設備控制（LIVE-07 警報器 / 補光燈、DEV-11 重新開機）：有這個能力才出現（lib/caps.ts controlCaps；SPEC #contract-app-ui）——
// 獨立設備看生效能力的 kinio_siren / kinio_floodlight，通道看 kinio_channel.abilities 的 siren / light；
// 從沒宣告能力的舊設備退回看 reported_clusters（原本的做法）。
// 命令經 thingProxy，權限由 app-api 依命令 id 驗（警報器 / 補光燈 = device.control；重開機 = owner / admin）。
// 按鈕只照「我的設備」的 actions（SPEC #device-permissions-ui）：沒有 device.control 不出現；重開機看 canReboot（呼叫端傳 can_manage）。
// 雙向對講在即時畫面的格子上（LiveTile）；對時 / 拉日誌（DEV-11）平台還沒有對應的流程，這裡不做假的按鈕。
import { KinioError } from '@kinio/web-sdk';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { can, type VmsDevice } from '../../lib/devices';
import { controlCaps } from '../../lib/caps';
import { deviceCommand, INTERACTIVE_WAIT_SECONDS } from '../../lib/deviceApi';
import { useConfirm, type ConfirmOptions } from '../../components/ConfirmDialog';
import { MoreMenu } from '../../components/MoreMenu';

/** 響警報器後沒有回報時，當它響這麼久（設備預設鳴叫時長 30 秒）。 */
const SIREN_ASSUME_MS = 30_000;

/**
 * compact：影像牆放大單路時（LIVE-07、P1-2）——「響警報器」收進〔⋯ 更多〕並走第 2 級確認，不放在第一排、不跟「停止警報」並排。
 * 〔停止警報〕只在警報器正在響時出現（VMS UX 評審第三輪 R3-P1-8）：shadow 報 kinio_siren.sounding（sounding 參數），
 * 或這個畫面剛按過〔響警報器〕（30 秒內，沒有回報的設備也看得到停止鈕）。
 */
export function DeviceControls({ dev, canReboot, compact, sounding }: { dev: VmsDevice; canReboot?: boolean; compact?: boolean; sounding?: boolean }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);
  const [sirenUntil, setSirenUntil] = useState(0);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!sirenUntil) return;
    const id = window.setTimeout(() => (setSirenUntil(0), tick((n) => n + 1)), Math.max(0, sirenUntil - Date.now()));
    return () => window.clearTimeout(id);
  }, [sirenUntil]);
  const [msg, setMsg] = useState<string | null>(null);
  const caps = controlCaps(dev);
  const control = can(dev, 'device.control');
  const { ask, dialog } = useConfirm();
  const run = async (id: string, confirm?: ConfirmOptions) => {
    if (confirm && !(await ask(confirm))) return;
    setBusy(id);
    setMsg(null);
    try {
      await deviceCommand(dev.apiEndpoint, dev.homeId, dev.aid, id, undefined, id === 'kinio_system.reboot' ? undefined : INTERACTIVE_WAIT_SECONDS);
      if (id === 'kinio_siren.trigger') setSirenUntil(Date.now() + SIREN_ASSUME_MS);
      if (id === 'kinio_siren.stop') setSirenUntil(0);
      setMsg(t('dctl.sent'));
    } catch (e) {
      // 命令錯誤 {error, message, detail: {command}}：503 DEVICE_OFFLINE / DEVICE_WAKING / DEVICE_ASLEEP、504 COMMAND_TIMEOUT、409 RENEW_ONLY
      const c = e instanceof KinioError ? e.code : String(e);
      setMsg(t(`dctl.err_${c}`, { defaultValue: e instanceof KinioError ? e.message : c }));
    } finally {
      setBusy(null);
    }
  };
  const btn = (id: string, label: string, cls = 'btn btn-sm', confirm?: ConfirmOptions) => (
    <button key={id} className={cls} disabled={busy !== null || dev.presence === 'offline'} onClick={() => void run(id, confirm)} data-cmd={id}>
      {label}
    </button>
  );
  const sirenAsk: ConfirmOptions = { title: t('dctl.siren_confirm', { name: dev.name }), body: t('dctl.siren_confirm_body'), action: t('dctl.siren_action') };
  if (compact) {
    const idle = busy !== null || dev.presence === 'offline';
    const more = control && caps.siren && !idle ? [{ key: 'siren', label: t('dctl.siren_action'), danger: true, onClick: () => void run('kinio_siren.trigger', sirenAsk) }] : [];
    const ringing = !!sounding || sirenUntil > Date.now();
    const row = [
      ...(control && caps.siren && ringing ? [btn('kinio_siren.stop', t('dctl.siren_off'), 'btn btn-sm btn-danger')] : []),
      ...(control && caps.light ? [btn('kinio_floodlight.trigger', t('dctl.light_on')), btn('kinio_floodlight.stop', t('dctl.light_off'))] : []),
    ];
    if (!row.length && !more.length) return null;
    return (
      <div className="toolbar wrap device-controls" data-testid="device-controls">
        <span className="spacer" />
        {row}
        <MoreMenu label={t('dctl.more')} items={more} />
        {msg && <span className="small muted">{msg}</span>}
        {dialog}
      </div>
    );
  }
  const items = [
    ...(control && caps.siren ? [btn('kinio_siren.trigger', t('dctl.siren_on'), 'btn btn-sm btn-danger'), btn('kinio_siren.stop', t('dctl.siren_off'))] : []),
    ...(control && caps.light ? [btn('kinio_floodlight.trigger', t('dctl.light_on')), btn('kinio_floodlight.stop', t('dctl.light_off'))] : []),
    ...(canReboot && !dev.parentAid ? [btn('kinio_system.reboot', t('dctl.reboot'), 'btn btn-sm', { title: t('dctl.reboot_confirm', { name: dev.name }), action: t('common.action_reboot') })] : []),
  ];
  if (!items.length) return null;
  return (
    <div className="toolbar wrap device-controls" data-testid="device-controls">
      {items}
      {msg && <span className="small muted">{msg}</span>}
      {dialog}
    </div>
  );
}
